import { ValidationResult } from '../types.js';
import { LlmailConfig, ResolvedConfig } from './config.js';

/**
 * Validates llmail configuration.
 * This is a focused utility that only handles config validation.
 * It ensures the config has the required structure and valid values.
 */
export class ConfigValidator {
  /**
   * Validate raw llmail configuration
   */
  validateConfig(config: LlmailConfig): ValidationResult {
    const errors: string[] = [];

    // Validate required arrays
    if (!Array.isArray(config.types_list)) {
      errors.push('types_list must be an array');
    }

    // Validate array contents if present
    if (config.types_list?.some(type => typeof type !== 'string' || !type.match(/^[a-z0-9]+$/))) {
      errors.push('types_list must contain only lowercase alphanumeric strings without hyphens');
    }
    if (config.active_states?.some(state => typeof state !== 'string' || !state.match(/^[a-z0-9]+$/))) {
      errors.push('active_states must contain only lowercase alphanumeric strings without hyphens');
    }
    if (config.inactive_reasons?.some(reason => typeof reason !== 'string' || !reason.match(/^[a-z0-9]+$/))) {
      errors.push('inactive_reasons must contain only lowercase alphanumeric strings without hyphens');
    }

    // Check for overlapping states if both arrays are present
    if (config.active_states?.length > 0 && config.inactive_reasons?.length > 0) {
      const activeStates = new Set(config.active_states);
      for (const reason of config.inactive_reasons) {
        if (activeStates.has(reason)) {
          errors.push('States cannot be both active and inactive');
          break;
        }
      }
    }

    // Validate required paths
    if (!config.issues_dir) {
      errors.push('issues_dir is required');
    }
    if (!config.inbox_dir) {
      errors.push('inbox_dir is required');
    }

    // Validate plugins if present
    if (config.plugins !== undefined) {
      if (typeof config.plugins === 'string') {
        // Single plugin string format
        if (!config.plugins.match(/^[a-z0-9-]+$/)) {
          errors.push('Plugin name must be lowercase alphanumeric with hyphens');
        }
      } else if (Array.isArray(config.plugins)) {
        // Array format
        for (const plugin of config.plugins) {
          if (typeof plugin === 'string') {
            // String array format
            if (!plugin.match(/^[a-z0-9-]+$/)) {
              errors.push('Plugin names must be lowercase alphanumeric with hyphens');
            }
          } else if (typeof plugin === 'object' && plugin !== null) {
            // Object format with config
            if (!plugin.name?.match(/^[a-z0-9-]+$/)) {
              errors.push('Plugin names must be lowercase alphanumeric with hyphens');
            }
            if (plugin.config !== undefined && (typeof plugin.config !== 'object' || plugin.config === null)) {
              errors.push('Plugin config must be an object');
            }
          } else {
            errors.push('Invalid plugin configuration format');
          }
        }
      } else {
        errors.push('Invalid plugins configuration format');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  /**
   * Validate resolved configuration
   * This is used after the config has been normalized and paths resolved
   */
  validateResolvedConfig(config: ResolvedConfig): ValidationResult {
    const errors: string[] = [];

    // Validate required arrays are present
    if (!Array.isArray(config.types_list)) {
      errors.push('Resolved config must have types_list array');
    }
    if (!Array.isArray(config.active_states)) {
      errors.push('Resolved config must have active_states array');
    }
    if (!Array.isArray(config.inactive_reasons)) {
      errors.push('Resolved config must have inactive_reasons array');
    }

    // Validate required paths are absolute
    if (!config.issues_dir?.startsWith('/')) {
      errors.push('Resolved issues_dir must be absolute path');
    }
    if (!config.inbox_dir?.startsWith('/')) {
      errors.push('Resolved inbox_dir must be absolute path');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }
} 